import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { laneFlow, MOUTH_LANES, sendaGoal, slideShadow, type BattleState } from '@somnhold/sim';
import { pinMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { h, setStyle } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';

/** An iron wedge, point down (the large wedges' own mark). */
const WEDGE = '<svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4 1.5h8l-1.2 3H5.2z" fill="currentColor" opacity="0.7"/><path d="M5.2 5h5.6L8 15z" fill="currentColor"/></svg>';

/** Where the avalanche's marks hang (render's SlidePlace anchors, world points). */
export interface SlideAnchors {
  rocks: THREE.Vector3[];
  mouth: THREE.Vector3 | null;
  doors: THREE.Vector3[];
}

/**
 * The avalanche on screen (docs/sendas/guerrero.md, chapter 4), inside a path's own plate (game/senda-hud.ts) and over
 * the board, kept short so a phone's plate stays under a quarter of the screen:
 *
 * - the loose slope: Kel up the climb; the fronts (three pips) and the next one's shadow counting down, red and
 *   pulsing while it is out («¡La sombra! Atrás de una piedra»); the giant's life once it wakes.
 * - the ice cave: the three rooms as pips, and the rule in a line («De frente no les entra nada»).
 * - the large wedges: the wedges in hand; the foot of the slope as seven cells (how much snow comes down each lane
 *   with the wedges as they are; the three over the mouth framed, red when snow comes down them); the fall's clock
 *   and the cave's hits. Over the board a mark on each of the nine rocks, the grain's way on it, tappable (the hero
 *   goes and drives a wedge in or pulls it out), its ring filling as it works; red when the snow breaks it.
 *
 * Plain DOM written only when it changes (docs/RENDIMIENTO.md).
 */
export class SlideHud {
  readonly panel: HTMLElement;
  private kind: 'loose' | 'cave' | 'lanes';
  private v = new THREE.Vector3();
  // The loose slope.
  private upFill = h('i');
  private upN = h('b.bh-n');
  private fronts: HTMLElement[] = [];
  private frontState = h('span.sl-front');
  private frontRow = h('div.bh-row.sl-frontrow');
  private giantRow = h('div.bh-row');
  private giantFill = h('i');
  // The cave.
  private rooms: HTMLElement[] = [];
  // The large wedges.
  private hand: HTMLElement[] = [];
  private foot: { el: HTMLElement; n: HTMLElement }[] = [];
  private clock = h('span.sl-clock');
  private hits = h('b.bh-n');
  private marks: { el: HTMLButtonElement; arrow: HTMLElement }[] = [];
  private key = '';

  constructor(
    s: BattleState,
    private board: HTMLElement,
    private mark: (at: number) => void,
  ) {
    const r = s.rules?.senda;
    this.kind = r?.cave ? 'cave' : r?.slide?.lanes ? 'lanes' : 'loose';
    this.panel = this.kind === 'cave' ? this.buildCave(s) : this.kind === 'lanes' ? this.buildLanes(s) : this.buildLoose(s);
  }

  /* ---------- the loose slope ---------- */

  private buildLoose(s: BattleState): HTMLElement {
    const n = s.rules?.senda?.slide?.loose?.fronts.length ?? 3;
    this.fronts = Array.from({ length: n }, () => h('i'));
    this.frontRow.append(h('span.bh-ico', { html: ICONS.rime }), h('span.bh-label', {}, t('senda.slide.front')), h('span.sl-pips', {}, ...this.fronts), this.frontState);
    this.giantRow.append(h('span.bh-ico', { html: ICONS.skull }), h('span.bh-label', {}, t('senda.slide.giant')), h('div.bar.bh-bar.fh-hp', {}, this.giantFill));
    this.giantRow.hidden = true;
    return h(
      'div.bh.slp-loose',
      {},
      h('div.bh-row', {}, h('span.bh-ico', { html: ICONS.walk }), h('span.bh-label', {}, t('senda.slide.climb')), h('div.bar.bh-bar', {}, this.upFill), this.upN),
      this.frontRow,
      this.giantRow,
    );
  }

  private updateLoose(s: BattleState): void {
    const g = sendaGoal(s);
    const k = Math.max(0, Math.min(1, g.done / Math.max(1, g.need)));
    setStyle(this.upFill, 'width', `${(k * 100).toFixed(1)}%`);
    const pct = `${Math.floor(k * 100)} %`;
    if (this.upN.textContent !== pct) this.upN.textContent = pct;
    const w = s.senda?.slide;
    const r = s.rules?.senda?.slide;
    if (!w || !r) return;
    this.fronts.forEach((p, i) => p.classList.toggle('on', i < w.n - (w.on ? 1 : 0)));
    const shadow = slideShadow(s, r, w);
    const text = w.on ? t('senda.slide.coming') : shadow ? t('senda.slide.shadow') : w.next >= 0 ? t('senda.slide.frontIn', { s: Math.max(0, Math.ceil((w.next - r.warn - s.tick) / TICK_HZ)) }) : '';
    if (this.frontState.textContent !== text) this.frontState.textContent = text;
    this.frontRow.classList.toggle('warn', shadow);
    const e = w.giant ? s.enemies.find((x) => x.id === w.giant) : undefined;
    const awake = !!e && e.hp > 0 && w.awake;
    this.giantRow.hidden = !awake;
    if (awake && e) setStyle(this.giantFill, 'width', `${((100 * e.hp) / e.maxHp).toFixed(1)}%`);
  }

  /* ---------- the cave ---------- */

  private buildCave(s: BattleState): HTMLElement {
    const n = s.rules?.senda?.cave?.rooms.length ?? 3;
    this.rooms = Array.from({ length: n }, (_, i) => h('i', {}, String(i + 1)));
    return h(
      'div.bh.slp-cave',
      {},
      h('div.bh-row', {}, h('span.bh-ico', { html: ICONS.walk }), h('span.bh-label', {}, t('senda.slide.rooms')), h('span.sl-rooms', {}, ...this.rooms)),
      h('p.sl-rule', {}, h('span', { html: ICONS.shield }), t('senda.slide.guard')),
    );
  }

  private updateCave(s: BattleState): void {
    const st = s.senda?.cave;
    if (!st) return;
    this.rooms.forEach((el, i) => {
      el.classList.toggle('on', !!st.cleared[i]);
      el.classList.toggle('now', !st.cleared[i] && !!st.awake[i]);
    });
  }

  /* ---------- the large wedges ---------- */

  private buildLanes(s: BattleState): HTMLElement {
    const L = s.rules?.senda?.slide?.lanes;
    const n = L?.wedges ?? 4;
    this.hand = Array.from({ length: n }, () => h('i', { html: WEDGE }));
    const mouth = MOUTH_LANES as readonly number[];
    this.foot = Array.from({ length: 7 }, (_, i) => {
      const nEl = h('b');
      return { el: h(`span${mouth.includes(i) ? '.mouth' : ''}`, {}, nEl), n: nEl };
    });
    // The rocks' marks over the board.
    const rocks = L ? L.rows.flatMap((row) => row.rocks) : [];
    this.marks = rocks.map((rk, i) => {
      const arrow = h('span.sl-grain', {}, rk.dir > 0 ? '→' : '←');
      const el = h(
        'button.sl-rock',
        { type: 'button', 'data-test': `slide-rock-${i}`, 'aria-label': t('senda.slide.rockHint'), onpointerup: (e: Event) => (e.stopPropagation(), this.mark(i)) },
        h('span.sl-ring'),
        arrow,
      ) as HTMLButtonElement;
      this.board.append(el);
      return { el, arrow };
    });
    return h(
      'div.bh.slp-lanes',
      {},
      h('div.bh-row', {}, h('span.bh-label', {}, t('senda.slide.wedges')), h('span.sl-hand', {}, ...this.hand), h('span.sl-sep'), h('span.bh-label.sl-cave', {}, t('senda.slide.cave')), this.hits),
      h('div.bh-row', {}, h('span.bh-label', {}, t('senda.slide.foot')), h('span.sl-foot', {}, ...this.foot.map((f) => f.el))),
      h('div.bh-row', {}, this.clock),
    );
  }

  private updateLanes(s: BattleState): void {
    const w = s.senda?.slide;
    const L = s.rules?.senda?.slide?.lanes;
    if (!w || !L) return;
    const key = `${w.shut.join('')}|${w.hand}|${w.touches}|${w.at}`;
    if (key !== this.key) {
      this.key = key;
      this.hand.forEach((el, i) => el.classList.toggle('on', i < w.hand));
      const f = laneFlow(L.rows.map((x) => x.rocks), w.shut);
      const foot = f.seg[f.seg.length - 1] as number[];
      this.foot.forEach((c, i) => {
        const n = foot[i] as number;
        c.n.textContent = n ? String(n) : '·';
        c.el.classList.toggle('snow', n > 0);
      });
      this.marks.forEach((m, i) => {
        m.el.classList.toggle('shut', w.shut[i] === 1);
        m.el.classList.toggle('broke', !!f.broke[i]);
      });
      const hits = t('senda.slide.touches', { n: w.touches, of: L.touches });
      if (this.hits.textContent !== hits) this.hits.textContent = hits;
      this.hits.classList.toggle('bad', w.touches > 0);
    }
    this.marks.forEach((m, i) => {
      const sent = w.at === i;
      m.el.classList.toggle('sent', sent);
      m.el.style.setProperty('--k', sent ? Math.min(1, w.held / Math.max(1, L.stand)).toFixed(2) : '0');
      m.el.disabled = s.result !== 'running';
    });
    const left = w.next >= 0 ? Math.max(0, Math.ceil((w.next - s.tick) / TICK_HZ)) : 0;
    const text = w.over >= 0 ? '' : w.on ? t('senda.slide.falling') : w.touches > 0 ? t('senda.slide.again', { s: left }) : t('senda.slide.fall', { s: left });
    if (this.clock.textContent !== text) this.clock.textContent = text;
    this.clock.classList.toggle('soon', w.on || (w.next >= 0 && w.next - s.tick < 15 * TICK_HZ));
  }

  update(s: BattleState): void {
    if (this.kind === 'loose') this.updateLoose(s);
    else if (this.kind === 'cave') this.updateCave(s);
    else this.updateLanes(s);
  }

  place(camera: THREE.Camera, w: number, ht: number, a: SlideAnchors | null): void {
    if (!a || this.kind !== 'lanes') return;
    this.marks.forEach((m, i) => {
      const p = a.rocks[i];
      if (!p) return setStyle(m.el, 'display', 'none');
      this.v.copy(p).project(camera);
      if (this.v.z > 1) return setStyle(m.el, 'display', 'none');
      setStyle(m.el, 'display', '');
      pinMark(m.el, (this.v.x * 0.5 + 0.5) * w, (-this.v.y * 0.5 + 0.5) * ht);
    });
  }
}
